import "./PlaceOrderWizardComponent.scss";
import template from "./PlaceOrderWizardComponent.html";
import {AdviceTypes} from "./models/AdviceTypes";
import {defaultAdvice, OrderAdvice} from "./models/OrderAdvice";
import {BaseStrategy} from "../types/WhatIf";
import {OptionsWizardOrder, OptionsWizardOrderLeg, OptionsWizardTmcOrder} from "../types/OptionsTicket";
import {TmcCodeResponse, TmcLeg} from "../services/models/TmcRequests";
import {PortfolioPositions, PortfolioType} from "../../../models/Portfolio";
import {OrderVettingService} from "../services/OrderVettingService";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {IOnDestroy, IOnInit, IScope} from "angular";
import {OrderRequest} from "./models/OrderRequest";
import {DuplicateOrderSummaries} from "../types/DuplicateOrdersVettingResponse";
import {DuplicateOrdersNotificationDialog} from "./dialog/DuplicateOrdersNotificationDialog";
import {fromTmcTicket} from "../services/models/PlaceOrderRequest";
import {IModalInstanceService} from "angular-ui-bootstrap";
import {AccountSource} from "../../../../account/types/AccountSource";
import {AccountStore} from "../../../../account/store/AccountStore";
import {ReadonlyAccountStore} from "../../../../account/store/ReadonlyAccountStore";
import {CurrentUserService} from "../../../../services/user/CurrentUserService";
import {isNotNull} from "../../../../js/util/UtilFunctions";
import {PortfolioService} from "../../../service/PortfolioService";
import {AccountBriefRecord} from "../../../../account/types/AccountBriefRecord";
import {Pair, PairOf} from "../../../../types/GenericTypes";
import {OrderTicketUtil} from "../util/OrderTicketUtil";
import {PlaceOrderService} from "../services/PlaceOrderService";
import {DuplicateOrderService} from "../services/DuplicateOrderService";
import {OrderAdviceService} from "../services/OrderAdviceService";
import {ObservableWithLazyLatest} from "../../../../js/classes/lazy-observable/ObservableWithLazyLatest";

interface Resolve {
    readonly whatIf: BaseStrategy;
    readonly fromTradeIdea: boolean;
    readonly targetPrice?: number;
}

export const PlaceOrderWizard = "placeOrderWizard";

export function placeOrderWizard() {
    return {
        template,
        bindings: {
            modalInstance: "<",
            resolve: "<"
        },
        controllerAs: "ctrl",
        controller: PlaceOrderWizardComponentController
    };
}

export class PlaceOrderWizardComponentController implements IOnInit, IOnDestroy {

    private readonly modalInstance: IModalInstanceService;
    private readonly resolve: Resolve;

    public readonly portfolio: PortfolioPositions;
    public whatIf: BaseStrategy;
    public fromTradeIdea: boolean;
    public targetPrice?: number | null;

    public isTmc = false;
    public adviceDetails?: OrderAdvice | null;
    public order: OptionsWizardTmcOrder;
    public orders: Array<OptionsWizardOrder>;

    public readonly currentAccountBrief$ = new ReplaySubject<AccountBriefRecord>(1);

    public readonly currentAccount$ = new ReplaySubject<PortfolioPositions>(1);

    public readonly tmcResponse$ = new ReplaySubject<TmcCodeResponse>(1);

    public readonly hasAdvice$ = new ReplaySubject<boolean>(1);

    public readonly adviceTypes$ = new ReplaySubject<AdviceTypes>(1);

    public readonly accountSource: AccountSource;

    public readonly processingOrder$ = new BehaviorSubject(false);

    private readonly isAdviser$ = new ReplaySubject<boolean>(1);

    private readonly placeOrder$ = new ReplaySubject<boolean>(1);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly $scope: IScope,
                       private readonly currentUserService: CurrentUserService,
                       private readonly orderVettingService: OrderVettingService,
                       private readonly duplicateOrderService: DuplicateOrderService,
                       private readonly duplicateOrdersNotificationDialog: DuplicateOrdersNotificationDialog,
                       private readonly placeOrderService: PlaceOrderService,
                       private readonly portfolioService: PortfolioService,
                       private readonly orderAdviceService: OrderAdviceService,
                       accountStore: AccountStore) {
        this.accountSource = ReadonlyAccountStore.create(accountStore);
        this.watchAdviceTypes();
    }

    public async $onInit() {
        this.whatIf = this.resolve.whatIf;
        this.fromTradeIdea = this.resolve.fromTradeIdea;
        this.targetPrice = this.resolve.targetPrice;
        this.isTmc = this.whatIf.tmc && this.whatIf.trades.length > 1;
        this.watchPlaceOrder();

        this.currentUserService
            .current
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((user) => user.isAdviser())
            .subscribe((isAdviser) => this.isAdviser$.next(isAdviser));

        this.accountSource
            .current
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .map((account) => account.id)
            .switchMap((id) => this.fetchPositions(id))
            .subscribe((positions) => this.currentAccount$.next(positions));

        this.accountSource
            .current
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .subscribe((current) => this.currentAccountBrief$.next(current));

        this.currentAccountBrief$
            .switchMap((brief) => this.retrieveTmcCode(brief))
            .subscribe((response) => {
                this.tmcResponse$.next(response);
                if (this.order != null) {
                    this.order.ticket.code = response.code;
                    this.order.ticket.buy = !response.opposite;
                }
            });

        this.accountSource
            .current
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .map((a) => a.id)
            .switchMap((id) => this.fetchAdvice(id))
            .subscribe((advice) => this.adviceTypes$.next(advice));

        Observable.combineLatest(this.accountSource.current, this.isAdviser$)
                  .takeUntil(this.destroyed$)
                  .filter(([portfolio]) => isNotNull(portfolio))
                  .map(([portfolio, isAdviser]) => isAdviser && portfolio!.portfolioType === PortfolioType.BROKER)
                  .subscribe((hasAdvice) => this.hasAdvice$.next(hasAdvice));
    }

    private fetchAdvice(portfolioId: number): Observable<AdviceTypes> {
        return Observable.of(portfolioId)
                         .distinctUntilChanged()
                         .flatMap((id) => this.orderAdviceService.get(id))
                         .catch((err) => Observable.empty<AdviceTypes>());
    }

    private watchAdviceTypes() {
        const $source = ObservableWithLazyLatest.create(this.adviceTypes$, this.currentUserService.current)
                                                .map(([types, user]) => defaultAdvice(types, user));

        this.hasAdvice$
            .distinctUntilChanged()
            .takeUntil(this.destroyed$)
            .switchMap((hasAdvice) => hasAdvice ? $source : Observable.of(null))
            .subscribe((advice) => {
                this.$scope.$applyAsync(() => {
                    this.adviceDetails = advice;
                });
            });
    }

    private fetchPositions(accountId: number): Observable<PortfolioPositions> {
        return Observable.of(accountId)
                         .flatMap((id) => this.portfolioService.portfolio(id))
                         .catch(() => Observable.empty<PortfolioPositions>());
    }

    private retrieveTmcCode(brief: AccountBriefRecord): Observable<TmcCodeResponse> {
        if (!this.whatIf.tmc) {
            return Observable.empty();
        } else if (brief.portfolioType === PortfolioType.SIMULATED) {
            const legs = OrderTicketUtil.tmcLegs(this.whatIf);
            return Observable.of(legs)
                             .flatMap((l) => this.placeOrderService.simulatedTmcCode(l))
                             .catch((err) => Observable.empty<TmcCodeResponse>());
        } else {
            const legs = OrderTicketUtil.tmcLegs(this.whatIf);
            return Observable.of(legs)
                             .flatMap((l) => this.placeOrderService.tmcCode(l, brief.id))
                             .catch((err) => Observable.empty<TmcCodeResponse>());
        }
    }

    private watchPlaceOrder() {
        this.placeOrder$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .throttleTime(1000)
            .map(() => this.buildOrderRequest())
            .withLatestFrom(this.accountSource.current)
            .switchMap(([request, account]) => this.validateRequest(request, account))
            .subscribe(([request, accountId]) => {
                this.modalInstance.close({portfolioId: accountId, trades: request.trades});
            });
    }

    private buildOrderRequest(): OrderRequest {
        if (this.isTmc) {
            const ticket = this.order.ticket;
            ticket.advice = this.adviceDetails;
            ticket.legs = this.order.legs.map(this.toTmcLeg.bind(this));
            return {
                tmc: true,
                trades: [ticket]
            };
        } else {
            const trades = this.orders.map(o => o.ticket);
            trades.forEach((trade) => trade.advice = this.adviceDetails);
            return {
                tmc: false,
                trades: trades as any // TODO This needs fixing at some point, but the entire wizard needs to be replaced anyway...
            };
        }
    }

    private toTmcLeg(leg: OptionsWizardOrderLeg): TmcLeg {
        return {
            code: leg.code,
            ratio: leg.ratio
        };
    }

    /*
     * Returns Empty to signal "should not proceed", which should not close the wizard,
     * which is really just a hack since we need the request object in the next step of the chain and therefore need to
     * retain it after doing this check
     */
    private validateRequest(request: OrderRequest, account: AccountBriefRecord | null): Observable<Pair<OrderRequest, number>> {
        if (account == null) {
            return Observable.empty();
        }
        this.processingOrder$.next(true);
        const placeOrderRequests = request.trades.map(fromTmcTicket);
        const result = this.duplicateOrderService.validate(account.id, placeOrderRequests);
        return Observable.fromPromise(result)
                         .flatMap((res) => this.proceedIfDuplicates(res.duplicates))
                         .filter((acknowledged) => acknowledged)
                         .map(() => PairOf(request, account.id))
                         .finally(() => this.processingOrder$.next(false));
    }

    private proceedIfDuplicates(duplicates: DuplicateOrderSummaries): Observable<boolean> {
        if (duplicates.length === 0) {
            return Observable.of(true);
        } else {
            return this.notifyDuplicates(duplicates);
        }
    }

    private notifyDuplicates(duplicates: DuplicateOrderSummaries): Observable<boolean> {
        return this.duplicateOrdersNotificationDialog.open(duplicates);
    }

    public placeOrder() {
        this.placeOrder$.next(true);
    }

    public cancel() {
        this.modalInstance.dismiss();
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
